import React from "react";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useTranslate,
} from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spin,
  Statistic,
  Typography,
  message,
  theme,
} from "antd";
import {
  DeleteOutlined,
  ExperimentOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { usePageStyles } from "../../../../shared/styles";

const { Title, Text, Paragraph } = Typography;

type DemoDataCounts = {
  accounts: number;
  users: number;
  orders: number;
  products: number;
  serviceProviders: number;
  creditPacks: number;
};

type DemoDataStatus = {
  hasDemoData: boolean;
  generatedAt: string | null;
  counts: DemoDataCounts;
};

export const DemoDataTab: React.FC = () => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const pageStyles = usePageStyles();
  const { token } = theme.useToken();

  const {
    data: statusResponse,
    isLoading,
    isFetching,
    refetch,
  } = useCustom<DemoDataStatus>({
    url: `${apiUrl}/demo-data/status`,
    method: "get",
  });

  const { mutate: generateDemoData, isLoading: isGenerating } =
    useCustomMutation<DemoDataStatus>();
  const { mutate: deleteDemoData, isLoading: isDeleting } =
    useCustomMutation<DemoDataStatus>();

  const status: DemoDataStatus | undefined = statusResponse?.data;
  const hasDemoData: boolean = Boolean(status?.hasDemoData);
  const counts: DemoDataCounts = status?.counts ?? {
    accounts: 0,
    users: 0,
    orders: 0,
    products: 0,
    serviceProviders: 0,
    creditPacks: 0,
  };

  const handleGenerate = () => {
    generateDemoData(
      {
        url: `${apiUrl}/demo-data/generate`,
        method: "post",
        values: {},
      },
      {
        onSuccess: () => {
          message.success(_t("configuration.demoData.generateSuccess"));
          refetch();
        },
        onError: () => {
          message.error(_t("configuration.demoData.generateError"));
        },
      },
    );
  };

  const handleDelete = () => {
    Modal.confirm({
      title: _t("configuration.demoData.deleteConfirmTitle"),
      content: _t("configuration.demoData.deleteConfirmContent"),
      icon: <ExclamationCircleOutlined />,
      okText: _t("configuration.demoData.deleteButton"),
      okType: "danger",
      cancelText: _t("buttons.cancel"),
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          deleteDemoData(
            {
              url: `${apiUrl}/demo-data`,
              method: "delete",
              values: {},
            },
            {
              onSuccess: () => {
                message.success(_t("configuration.demoData.deleteSuccess"));
                refetch();
                resolve();
              },
              onError: () => {
                message.error(_t("configuration.demoData.deleteError"));
                reject();
              },
            },
          );
        }),
    });
  };

  if (isLoading && !status) {
    return (
      <Card className="shadow-lg" style={pageStyles.stepperCard}>
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg" style={pageStyles.stepperCard}>
      <div className="flex items-start gap-3 mb-2">
        <ExperimentOutlined
          className="text-xl mt-1"
          style={{ color: token.colorPrimary }}
        />
        <div>
          <Title level={4} className="!mb-1 !font-semibold">
            {_t("configuration.demoData.title")}
          </Title>
          <Text type="secondary">
            {_t("configuration.demoData.subtitle")}
          </Text>
        </div>
      </div>

      {!hasDemoData ? (
        <div className="mt-6 space-y-4">
          <Paragraph style={{ color: token.colorTextSecondary }}>
            {_t("configuration.demoData.generateDescription")}
          </Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<ExperimentOutlined />}
            loading={isGenerating || isFetching}
            onClick={handleGenerate}
          >
            {_t("configuration.demoData.generateButton")}
          </Button>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <Alert
            type="warning"
            showIcon
            message={_t("configuration.demoData.deleteWarning")}
          />
          {status?.generatedAt && (
            <Text type="secondary" className="block">
              {_t("configuration.demoData.generatedAt")}:{" "}
              {new Date(status.generatedAt).toLocaleString()}
            </Text>
          )}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.accounts")}
                value={counts.accounts}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.users")}
                value={counts.users}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.orders")}
                value={counts.orders}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.products")}
                value={counts.products}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.serviceProviders")}
                value={counts.serviceProviders}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title={_t("configuration.demoData.counts.creditPacks")}
                value={counts.creditPacks}
              />
            </Col>
          </Row>
          <Button
            danger
            size="large"
            icon={<DeleteOutlined />}
            loading={isDeleting || isFetching}
            onClick={handleDelete}
          >
            {_t("configuration.demoData.deleteButton")}
          </Button>
        </div>
      )}
    </Card>
  );
};
